import { SECTION_ID } from '../../../shared/constants/sectionIds.js'
import { HERO_VIEW } from '../../../shared/constants/heroView.js'
import {
  getBuildingIdCadFromIdCad,
  getEntranceRefTierFromIdCad,
} from '../../../shared/utils/idCadUtils.js'
import { isDisplayable, isProductAccessible } from '../../../shared/utils/productUtils.js'

export const APP_ROUTE_PARAM = Object.freeze({
  PRODUCT: 'bim-product',
  BUILDING: 'bim-building',
  TIER: 'bim-tier',
  VIEW: 'bim-view',
  LEGACY_PRODUCT: 'apt',
})

export const APP_ROUTE_HASH = Object.freeze({
  [SECTION_ID.HERO]: '#bim-hero',
  [SECTION_ID.CATALOG]: '#bim-catalog',
  [SECTION_ID.TIERS]: '#bim-tiers',
  [SECTION_ID.PRODUCT]: '#bim-product',
})

const APP_ROUTE_HASH_TO_SECTION_ID = Object.freeze(
  Object.fromEntries(Object.entries(APP_ROUTE_HASH).map(([sectionId, hash]) => [hash, sectionId]))
)

const INTERNAL_ROUTE_PARAMS = new Set(Object.values(APP_ROUTE_PARAM))

function normalizeString(value) {
  const normalized = String(value ?? '').trim()
  return normalized.length > 0 ? normalized : null
}

function normalizeTierRef(value) {
  const normalized = normalizeString(value)
  if (!normalized) return null

  const parsed = Number(normalized)
  return Number.isInteger(parsed) ? parsed : null
}

function normalizeHeroRouteView(value) {
  const normalized = normalizeString(value)?.toLowerCase()
  if (!normalized) return null

  if (normalized === 'visualization') return 'visualization'
  if (normalized === 'model' || normalized === HERO_VIEW.SITE || normalized === HERO_VIEW.BUILDING) {
    return normalized
  }

  return null
}

function getRouteSectionId(hash) {
  const normalized = normalizeString(hash)?.toLowerCase()
  if (!normalized) return null
  return APP_ROUTE_HASH_TO_SECTION_ID[normalized] ?? null
}

function getAllowedSectionIds(allowedSectionIds) {
  return Array.isArray(allowedSectionIds) && allowedSectionIds.length > 0
    ? allowedSectionIds.filter(Boolean)
    : null
}

function getAllowedHeroViews(allowedHeroViews) {
  if (!Array.isArray(allowedHeroViews) || allowedHeroViews.length === 0) {
    return [HERO_VIEW.VISUALIZATION, HERO_VIEW.SITE, HERO_VIEW.BUILDING]
  }

  return allowedHeroViews.filter(Boolean)
}

function resolveDefaultHeroView(defaultHeroView, allowedHeroViews) {
  if (defaultHeroView && allowedHeroViews.includes(defaultHeroView)) return defaultHeroView
  return allowedHeroViews[0] ?? HERO_VIEW.VISUALIZATION
}

function resolveDefaultModelHeroView(defaultHeroView, allowedHeroViews) {
  if (
    (defaultHeroView === HERO_VIEW.SITE || defaultHeroView === HERO_VIEW.BUILDING) &&
    allowedHeroViews.includes(defaultHeroView)
  ) {
    return defaultHeroView
  }

  if (allowedHeroViews.includes(HERO_VIEW.SITE)) return HERO_VIEW.SITE
  if (allowedHeroViews.includes(HERO_VIEW.BUILDING)) return HERO_VIEW.BUILDING
  return resolveDefaultHeroView(defaultHeroView, allowedHeroViews)
}

function resolveHeroSectionView(routeView, { defaultHeroView, allowedHeroViews }) {
  const allowed = getAllowedHeroViews(allowedHeroViews)
  const normalizedRouteView = normalizeHeroRouteView(routeView)

  if (normalizedRouteView === 'visualization' && allowed.includes(HERO_VIEW.VISUALIZATION)) {
    return HERO_VIEW.VISUALIZATION
  }

  if (normalizedRouteView === HERO_VIEW.SITE && allowed.includes(HERO_VIEW.SITE)) {
    return HERO_VIEW.SITE
  }

  if (normalizedRouteView === HERO_VIEW.BUILDING && allowed.includes(HERO_VIEW.BUILDING)) {
    return HERO_VIEW.BUILDING
  }

  if (normalizedRouteView === 'model' || normalizedRouteView === HERO_VIEW.SITE || normalizedRouteView === HERO_VIEW.BUILDING) {
    return resolveDefaultModelHeroView(defaultHeroView, allowed)
  }

  return resolveDefaultHeroView(defaultHeroView, allowed)
}

function toRouteHeroView(sectionId, sectionView) {
  if (sectionId !== SECTION_ID.HERO || !sectionView) return null
  return sectionView === HERO_VIEW.VISUALIZATION ? 'visualization' : 'model'
}

function resolveProductBuildingId(product) {
  return normalizeString(product?.building_id) ?? getBuildingIdCadFromIdCad(product?.id_cad)
}

function resolveProductTierRef(product) {
  const explicitRefTier = normalizeTierRef(product?.ref_tier)
  return explicitRefTier ?? getEntranceRefTierFromIdCad(product?.id_cad)
}

function resolveRouteProductState(products, productRef, accessibility) {
  const matchedProduct = resolveRouteProduct(products, productRef)
  if (!matchedProduct) {
    return {
      resolvedProduct: null,
      contextProduct: null,
      blockedProduct: null,
      blockedProductReason: null,
    }
  }

  if (!isDisplayable(matchedProduct.visibility)) {
    return {
      resolvedProduct: null,
      contextProduct: matchedProduct,
      blockedProduct: matchedProduct,
      blockedProductReason: 'hidden',
    }
  }

  if (!isProductAccessible(matchedProduct, accessibility)) {
    return {
      resolvedProduct: null,
      contextProduct: matchedProduct,
      blockedProduct: matchedProduct,
      blockedProductReason: 'inaccessible',
    }
  }

  return {
    resolvedProduct: matchedProduct,
    contextProduct: matchedProduct,
    blockedProduct: null,
    blockedProductReason: null,
  }
}

function inferSectionId(explicitSectionId, { activeProduct, activeBuilding, activeTier, hasBlockedProduct }) {
  // Active product is the strongest explicit state signal — always opens product view.
  if (activeProduct) return SECTION_ID.PRODUCT

  // Blocked product (inaccessible/hidden): fall back to tiers context if present.
  if (hasBlockedProduct && (activeBuilding || activeTier != null)) return SECTION_ID.TIERS

  // Explicit hash is the destination intent — wins over building/tier state presence.
  if (explicitSectionId !== null) return explicitSectionId

  // No explicit destination: stay at Hero even if building/tier state is present.
  // building/tier alone are share-state, not a navigation trigger.
  return SECTION_ID.HERO
}

function ensureAllowedSectionId(sectionId, allowedSectionIds, routeState) {
  const allowed = getAllowedSectionIds(allowedSectionIds)
  if (!allowed) return sectionId

  const allowedSet = new Set(allowed)
  if (allowedSet.has(sectionId)) return sectionId

  if (routeState.activeProduct && allowedSet.has(SECTION_ID.PRODUCT)) return SECTION_ID.PRODUCT
  if ((routeState.activeBuilding || routeState.activeTier != null) && allowedSet.has(SECTION_ID.TIERS)) {
    return SECTION_ID.TIERS
  }
  if (allowedSet.has(SECTION_ID.CATALOG)) return SECTION_ID.CATALOG
  if (allowedSet.has(SECTION_ID.HERO)) return SECTION_ID.HERO

  return allowed[0] ?? sectionId
}

export function getCanonicalProductRouteRef(product) {
  return normalizeString(product?.id_original)
}

export function getRuntimeProductKey(product) {
  return normalizeString(product?.uuid) ?? normalizeString(product?.id_cad)
}

export function resolveRouteProduct(products, productRef) {
  const normalizedProductRef = normalizeString(productRef)
  if (!normalizedProductRef || !Array.isArray(products)) return null

  return products.find((product) => {
    const idOriginal = normalizeString(product?.id_original)
    const idCad = normalizeString(product?.id_cad)
    const uuid = normalizeString(product?.uuid)

    return normalizedProductRef === idOriginal || normalizedProductRef === idCad || normalizedProductRef === uuid
  }) ?? null
}

export function resolveRuntimeProduct(products, activeProduct) {
  const normalizedActiveProduct = normalizeString(activeProduct)
  if (!normalizedActiveProduct || !Array.isArray(products)) return null

  return products.find((product) => {
    const idCad = normalizeString(product?.id_cad)
    const uuid = normalizeString(product?.uuid)
    const runtimeKey = getRuntimeProductKey(product)

    return (
      normalizedActiveProduct === runtimeKey ||
      normalizedActiveProduct === uuid ||
      normalizedActiveProduct === idCad
    )
  }) ?? null
}

export function parseAppUrlState(locationLike) {
  const params = new URLSearchParams(locationLike?.search ?? '')
  const explicitProductRef = normalizeString(params.get(APP_ROUTE_PARAM.PRODUCT))
  const legacyProductRef = normalizeString(params.get(APP_ROUTE_PARAM.LEGACY_PRODUCT))

  return {
    sectionId: getRouteSectionId(locationLike?.hash ?? ''),
    sectionView: normalizeHeroRouteView(params.get(APP_ROUTE_PARAM.VIEW)),
    productRef: explicitProductRef ?? legacyProductRef,
    buildingId: normalizeString(params.get(APP_ROUTE_PARAM.BUILDING)),
    tierRef: normalizeTierRef(params.get(APP_ROUTE_PARAM.TIER)),
    source: {
      usedLegacyProductParam: !explicitProductRef && Boolean(legacyProductRef),
    },
  }
}

export function normalizeAppUrlState(
  rawState,
  {
    products = [],
    allowedSectionIds = null,
    accessibility = null,
    defaultHeroView = HERO_VIEW.VISUALIZATION,
    allowedHeroViews = null,
  } = {},
) {
  const {
    resolvedProduct,
    contextProduct,
    blockedProduct,
    blockedProductReason,
  } = resolveRouteProductState(products, rawState?.productRef, accessibility)

  const activeProduct = getRuntimeProductKey(resolvedProduct)
  const activeBuilding =
    normalizeString(rawState?.buildingId) ??
    (contextProduct ? resolveProductBuildingId(contextProduct) : null)
  const activeTier =
    normalizeTierRef(rawState?.tierRef) ??
    (contextProduct ? resolveProductTierRef(contextProduct) : null)

  const inferredSectionId = inferSectionId(rawState?.sectionId ?? null, {
    activeProduct,
    activeBuilding,
    activeTier,
    hasBlockedProduct: Boolean(blockedProduct),
  })

  const sectionId = ensureAllowedSectionId(
    inferredSectionId,
    allowedSectionIds,
    { activeProduct, activeBuilding, activeTier },
  )

  // Keep tier state if in Tiers/Product, OR if building/tier were explicitly passed in URL.
  // This preserves building/tier context when user provides them (e.g., ?bim-building=X&bim-tier=Y#bim-hero).
  const shouldKeepTierState = sectionId === SECTION_ID.TIERS || sectionId === SECTION_ID.PRODUCT || (rawState?.buildingId || rawState?.tierRef)
  const shouldKeepProductState = sectionId === SECTION_ID.PRODUCT && Boolean(activeProduct)
  const sectionView = sectionId === SECTION_ID.HERO
    ? resolveHeroSectionView(rawState?.sectionView, { defaultHeroView, allowedHeroViews })
    : null
  const routeSectionView = toRouteHeroView(sectionId, sectionView)
  const rawRouteSectionView = normalizeHeroRouteView(rawState?.sectionView)

  return {
    sectionId,
    sectionView,
    activeProduct: shouldKeepProductState ? activeProduct : null,
    activeBuilding: shouldKeepTierState ? activeBuilding : null,
    activeTier: shouldKeepTierState ? activeTier : null,
    canonicalProductRef: shouldKeepProductState ? getCanonicalProductRouteRef(resolvedProduct) : null,
    didResolveProduct: Boolean(resolvedProduct),
    blockedProductReason,
    routeFeedback: blockedProduct
      ? {
          kind: blockedProductReason,
          productCategory: normalizeString(blockedProduct?.product_category),
        }
      : null,
    shouldReplaceUrl:
      Boolean(blockedProductReason) ||
      Boolean(rawState?.source?.usedLegacyProductParam) ||
      (Boolean(rawState?.productRef) && shouldKeepProductState && rawState.productRef !== getCanonicalProductRouteRef(resolvedProduct)) ||
      (sectionId === SECTION_ID.HERO && rawRouteSectionView !== routeSectionView) ||
      (sectionId !== SECTION_ID.HERO && Boolean(rawRouteSectionView)),
  }
}

export function serializeAppUrlState(
  appState,
  locationLike,
  {
    products = [],
    allowedSectionIds = null,
    accessibility = null,
    defaultHeroView = HERO_VIEW.VISUALIZATION,
    allowedHeroViews = null,
  } = {},
) {
  const shouldUseProductRoute = appState?.sectionId === SECTION_ID.PRODUCT
  const resolvedProduct = shouldUseProductRoute
    ? resolveRuntimeProduct(products, appState?.activeProduct)
    : null

  const normalizedState = normalizeAppUrlState(
    {
      sectionId: appState?.sectionId ?? null,
      sectionView: appState?.sectionView ?? null,
      productRef: shouldUseProductRoute ? getCanonicalProductRouteRef(resolvedProduct) : null,
      buildingId: normalizeString(appState?.activeBuilding),
      tierRef: normalizeTierRef(appState?.activeTier),
      source: { usedLegacyProductParam: false },
    },
    { products, allowedSectionIds, accessibility, defaultHeroView, allowedHeroViews },
  )

  const nextParams = new URLSearchParams()
  for (const [key, value] of new URLSearchParams(locationLike?.search ?? '')) {
    if (INTERNAL_ROUTE_PARAMS.has(key)) continue
    nextParams.append(key, value)
  }

  if (normalizedState.sectionId === SECTION_ID.PRODUCT && normalizedState.canonicalProductRef) {
    nextParams.set(APP_ROUTE_PARAM.PRODUCT, normalizedState.canonicalProductRef)
  }

  // Always preserve building/tier share-state in URL, regardless of current section.
  // This allows returning to Tiers with preserved context from Hero/Catalog.
  if (normalizedState.activeBuilding) {
    nextParams.set(APP_ROUTE_PARAM.BUILDING, normalizedState.activeBuilding)
  }
  if (normalizedState.activeTier != null) {
    nextParams.set(APP_ROUTE_PARAM.TIER, String(normalizedState.activeTier))
  }

  if (normalizedState.sectionId === SECTION_ID.HERO) {
    const heroRouteView = toRouteHeroView(normalizedState.sectionId, normalizedState.sectionView)
    if (heroRouteView) {
      nextParams.set(APP_ROUTE_PARAM.VIEW, heroRouteView)
    }
  }

  const search = nextParams.toString()

  return {
    search: search ? `?${search}` : '',
    hash: APP_ROUTE_HASH[normalizedState.sectionId] ?? '',
  }
}
